import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, ArrowLeft } from "lucide-react";
import { api, BLOG_CATEGORIES, blogCategoryColor } from "../lib/config";
import useSEO from "../hooks/useSEO";

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  useSEO({
    title: post?.title || "Article",
    description: post?.seo_description || post?.excerpt,
    image: post?.cover_image,
    type: "article",
    jsonLd: post ? {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": post.title,
      "description": post.excerpt,
      "image": post.cover_image,
      "datePublished": post.created_at,
      "author": { "@type": "Organization", "name": "Passion Verre" },
      "publisher": { "@type": "Organization", "name": "Passion Verre" },
    } : null,
  });

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/blog/${slug}`);
        setPost(r.data);
      } catch { setPost(null); }
      setLoading(false);
    })();
  }, [slug]);

  if (loading) return <div className="pt-40 text-center text-[#DDD4B2]/60">Chargement…</div>;
  if (!post) return (
    <div className="pt-40 text-center" data-testid="blog-post-404">
      <p className="text-[#DDD4B2]/60 mb-4">Article introuvable.</p>
      <Link to="/blog" className="text-[#9A8640] underline">Retour au blog</Link>
    </div>
  );

  return (
    <article className="pt-32 pb-24 bg-[#171717]" data-testid="blog-post-page">
      <div className="max-w-3xl mx-auto px-6">
        <Link to="/blog" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#9A8640] mb-8">
          <ArrowLeft size={14} /> Retour au blog
        </Link>
        <span
          className="inline-block px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-white rounded-full mb-5"
          style={{ background: blogCategoryColor(post.category) }}
        >
          {BLOG_CATEGORIES.find(c => c.slug === post.category)?.label}
        </span>
        <h1 className="font-serif text-4xl sm:text-5xl text-white leading-tight mb-5">{post.title}</h1>
        <div className="flex items-center gap-2 text-sm text-[#DDD4B2]/60 mb-10">
          <Calendar size={14} />
          <span>{new Date(post.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span>
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-6 mb-12">
        <img src={post.cover_image} alt={post.title} loading="eager" decoding="async" className="w-full h-auto max-h-[600px] object-contain mx-auto" />
      </div>
      <div className="max-w-3xl mx-auto px-6">
        <div className="prose prose-invert max-w-none">
          {post.content.split("\n").map((p, i) => (
            <p key={i} className="text-base leading-relaxed text-[#DDD4B2]/85 mb-5 whitespace-pre-line">{p}</p>
          ))}
        </div>

        {Array.isArray(post.images) && post.images.length > 0 && (
          <div className="mt-12" data-testid="blog-images-gallery">
            <p className="text-xs uppercase tracking-[0.2em] text-[#9A8640] mb-5">Galerie</p>
            <div className="columns-1 sm:columns-2 gap-4 [column-fill:_balance]">
              {post.images.map((src, i) => (
                <a key={i} href={src} target="_blank" rel="noopener noreferrer" className="block mb-4 break-inside-avoid bg-[#0f0f0f] group overflow-hidden">
                  <img src={src} alt={`${post.title} — image ${i + 1}`} loading="lazy" decoding="async" className="w-full h-auto object-contain group-hover:opacity-90 transition-opacity" />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </article>
  );
};

export default BlogPostPage;
